import { useRef } from "react";
import { CalendarCheck, Briefcase, FolderArchive, Settings, ChevronRight, ShieldCheck, Download, Upload } from "lucide-react";
import { toast } from "sonner";
import { exportAllJSON, importJSON } from "../lib/storage";

const items = [
  { id: "nastavni", title: "Nastavni dan", desc: "Nova evidencija dežuranja", icon: CalendarCheck, testId: "mode-nastavni-dan-btn" },
  { id: "nenastavni", title: "Nenastavni dan", desc: "Praznici, usavršavanja, odmori", icon: Briefcase, testId: "mode-nenastavni-dan-btn" },
  { id: "arhiva", title: "Prethodne evidencije", desc: "Učitaj i uredi spremljene", icon: FolderArchive, testId: "mode-arhiva-btn" },
  { id: "postavke", title: "Postavke škole", desc: "Naziv škole i opcije", icon: Settings, testId: "mode-postavke-btn" },
];

export default function Sidebar({ active, onSelect, onDataChanged, schoolName }) {
  const fileRef = useRef(null);

  const handleImport = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = () => {
      try {
        const count = importJSON(reader.result);
        toast.success(`Uvezeno ${count} evidencija iz sigurnosne kopije`);
        onDataChanged();
      } catch {
        toast.error("Datoteka nije ispravna sigurnosna kopija");
      }
    };
    reader.readAsText(file);
    e.target.value = "";
  };

  return (
    <aside className="flex w-full shrink-0 flex-col justify-between border-b border-slate-200 bg-white p-4 shadow-sm sm:p-5 lg:sticky lg:top-0 lg:h-screen lg:w-72 lg:overflow-y-auto lg:border-b-0 lg:border-r">
      <div>
        <button onClick={() => onSelect("pocetna")} data-testid="sidebar-logo-btn" className="mb-5 flex w-full items-center gap-3 border-b border-slate-100 pb-5 text-left">
          <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-slate-900 text-white">
            <ShieldCheck className="h-6 w-6" />
          </div>
          <div className="min-w-0">
            <p className="font-heading text-base font-extrabold leading-tight text-slate-900">Dežurko</p>
            <p className="truncate text-[11px] text-slate-500">{schoolName || "Evidencija dežuranja"}</p>
          </div>
        </button>

        <p className="mb-2.5 px-1 text-[11px] font-semibold uppercase tracking-wider text-slate-400">Glavni izbornik</p>
        <nav className="space-y-2">
          {items.map((item) => {
            const Icon = item.icon;
            const isActive = active === item.id;
            return (
              <button
                key={item.id}
                data-testid={item.testId}
                onClick={() => onSelect(item.id)}
                className={`group flex w-full items-center justify-between rounded-xl border p-3.5 text-left transition-all active:scale-[0.99] ${
                  isActive
                    ? "border-2 border-sky-600 bg-sky-50/70 shadow-sm"
                    : "border-slate-200/80 bg-white hover:border-sky-300 hover:bg-slate-50"
                }`}
              >
                <div className="flex items-center gap-3">
                  <div className={`flex h-9 w-9 items-center justify-center rounded-lg ${isActive ? "bg-sky-600 text-white" : "bg-sky-50 text-sky-600"}`}>
                    <Icon className="h-4.5 w-4.5" style={{ width: 18, height: 18 }} />
                  </div>
                  <div>
                    <p className={`text-sm font-semibold ${isActive ? "text-sky-950" : "text-slate-800"}`}>{item.title}</p>
                    <p className="text-[11px] text-slate-500">{item.desc}</p>
                  </div>
                </div>
                <ChevronRight className={`h-4 w-4 transition-transform ${isActive ? "text-sky-600" : "text-slate-300 group-hover:translate-x-0.5"}`} />
              </button>
            );
          })}
        </nav>
      </div>

      <div className="mt-6 space-y-2 border-t border-slate-100 pt-4">
        <p className="px-1 text-[11px] font-semibold uppercase tracking-wider text-slate-400">Sigurnosna kopija</p>
        <div className="flex gap-2">
          <button
            data-testid="quick-export-json-btn"
            onClick={() => { exportAllJSON(); toast.success("Sigurnosna kopija je preuzeta"); }}
            className="flex flex-1 items-center justify-center gap-1.5 rounded-xl border border-slate-200 bg-white px-3 py-2.5 text-xs font-semibold text-slate-600 transition-colors hover:bg-slate-50"
          >
            <Download className="h-3.5 w-3.5" /> Izvoz
          </button>
          <button
            data-testid="quick-import-json-btn"
            onClick={() => fileRef.current?.click()}
            className="flex flex-1 items-center justify-center gap-1.5 rounded-xl border border-slate-200 bg-white px-3 py-2.5 text-xs font-semibold text-slate-600 transition-colors hover:bg-slate-50"
          >
            <Upload className="h-3.5 w-3.5" /> Uvoz
          </button>
          <input ref={fileRef} type="file" accept=".json,application/json" className="hidden" onChange={handleImport} data-testid="import-file-input" />
        </div>
        <p className="px-1 text-[10px] leading-relaxed text-slate-400">Svi podaci ostaju samo u vašem pregledniku. Ništa se ne šalje na server.</p>
      </div>
    </aside>
  );
}
